import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PaymentsServiceApi } from '../services/PaymentsServiceApi';
import { useSubscription } from '../contexts/SubscriptionContext';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY, STRIPE_ENABLED } from '../config/stripe';
import './PaymentsPage.css';

// Use centralized config gate
let stripePromise;
if (STRIPE_ENABLED) {
  stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
}

export default function PaymentsPage() {
  const { subscription: sub, refresh, startActivationPolling, pollTimeout } = useSubscription();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true); // now for invoices + plans only
  const [actionMsg, setActionMsg] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [redirectingPlan, setRedirectingPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [banner, setBanner] = useState(null); // success / canceled
  const [plansFallback, setPlansFallback] = useState(false); // indicates server plans failed
  const [summary, setSummary] = useState(null);

  const activePlanAlias = sub?.planAlias || sub?.plan_alias || null;
  const activePriceId = sub?.priceId || sub?.items?.[0]?.price?.id || null;
  const cancelAtPeriodEnd = sub?.cancelAtPeriodEnd || sub?.cancel_at_period_end;
  const currentPeriodEnd = sub?.currentPeriodEnd || sub?.current_period_end;

  const formattedPeriodEnd = useMemo(() => {
    if (!currentPeriodEnd) return null;
    const ts = typeof currentPeriodEnd === 'number'
      ? currentPeriodEnd * 1000
      : Date.parse(currentPeriodEnd);
    return ts ? new Date(ts).toLocaleDateString() : null;
  }, [currentPeriodEnd]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      try { setSummary(await PaymentsServiceApi.getPaymentsSummary()); } catch {}
      try { const invoicesJson = await PaymentsServiceApi.listInvoices(20); if (Array.isArray(invoicesJson)) setInvoices(invoicesJson); } catch {}
      // subscription refresh handled separately
      if (!sub) await refresh();
    } finally { setLoading(false); }
  }, [sub, refresh]);

  // Load plans from server (/payments/plans). Fallback to static aliases if request fails or returns empty.
  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const serverPlans = await PaymentsServiceApi.listPlans();
      if (Array.isArray(serverPlans) && serverPlans.length) {
        const mapped = serverPlans.map(p => {
          const alias = p.alias || p.planAlias || p.id || p.key;
          const amount = p.amount || p.unitAmount; // may be undefined
          const currency = (p.currency || 'usd').toUpperCase();
          const interval = p.interval || p.recurringInterval || 'month';
          const label = p.label || p.name || (alias ? alias.charAt(0).toUpperCase() + alias.slice(1) + ' Plan' : 'Plan');
          const desc = p.description || p.desc || (interval ? `${interval.charAt(0).toUpperCase()}${interval.slice(1)} billing` : '');
          const displayPrice = typeof amount === 'number'
            ? `${(amount/100).toLocaleString(undefined,{style:'currency',currency})} / ${interval.substring(0,5)}`
            : ''; // server didn’t provide amount
          return {
            id: alias,
            alias,
            label,
            price: displayPrice || interval,
            desc,
            raw: p
          };
        });
        setPlans(mapped);
      } else {
        throw new Error('Empty plans');
      }
    } catch (err) {
      console.warn('Failed to load server plans, using fallback', err);
      setPlansFallback(true);
      // Fallback static aliases (must align with server accepted planAlias values, e.g. basic, pro)
      setPlans([
        { id: 'basic', alias: 'basic', label: 'Basic Monthly', price: '$9 / mo', desc: 'Core features' },
        { id: 'pro', alias: 'pro', label: 'Pro Monthly', price: '$29 / mo', desc: 'Advanced & priority AI' }
      ]);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); loadPlans(); }, [loadData, loadPlans]);

  // Auto-checkout flow for signup: if user redirected here with ?plan=<alias> and no subscription yet.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preselect = params.get('plan');
    if (!preselect) return;
    // If subscription exists, remove plan param quietly.
    if (sub?.subscriptionId) {
      const url = new URL(window.location.href); url.searchParams.delete('plan'); window.history.replaceState({}, '', url.toString());
      return;
    }
    // Wait until plans loaded before attempting auto-checkout
    if (plansLoading) return;
    // Ensure we have not already attempted redirect
    if (redirectingPlan || banner?.type === 'success') return;
    const match = plans.find(p => (p.alias || p.id) === preselect);
    if (match && STRIPE_ENABLED) {
      // Provide a short delay to allow user to see the page before redirecting (UX)
      setTimeout(() => { handleCheckout(match.alias || match.id); }, 400);
    }
  }, [plansLoading, plans, sub, redirectingPlan, banner]);

  // Success / canceled banner detection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      setBanner({ type: 'success', text: 'Payment successful. Activating subscription…' });
      // start polling for subscription status (webhook may take a moment)
      startActivationPolling();
    } else if (params.get('canceled')) {
      setBanner({ type: 'warn', text: 'Checkout canceled. You were not charged.' });
    } else if (params.get('portal')) {
      setBanner({ type: 'info', text: 'Returned from billing portal. Recent changes may take a moment to reflect.' });
    }
  }, [startActivationPolling]);

  // Polling now delegated to SubscriptionContext

  const handleCheckout = async (planAlias) => {
    setRedirectingPlan(planAlias);
    setActionMsg(null);
    try {
      if (!stripePromise) throw new Error('Stripe not loaded.');
      const idempotencyKey = 'chk:' + planAlias + ':' + Date.now();
      const successUrl = `${window.location.origin}/payments?success=1`;
      const cancelUrl = `${window.location.origin}/payments?canceled=1`;
      let data;
      try {
        data = await PaymentsServiceApi.createCheckoutSession({ planAlias, successUrl, cancelUrl, idempotencyKey });
      } catch (err) {
        console.error('[PaymentsPage] createCheckoutSession failed:', err);
        throw err;
      }
      // Support both sessionId pattern and legacy direct URL (per updated API description)
      if (data?.sessionId) {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) throw error;
      } else if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Checkout response missing sessionId or url');
      }
    } catch (e) {
      console.error('[PaymentsPage] Checkout error path reached:', e);
      setActionMsg(e.message || 'Checkout failed.');
      setRedirectingPlan(null);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    setActionMsg(null);
    try {
      await PaymentsServiceApi.cancelSubscription(true);
      setActionMsg('Set to cancel at period end.');
      await refresh();
    } catch (e) {
      setActionMsg(e.message || 'Failed.');
    } finally {
      setCanceling(false);
    }
  };

  const handlePortal = async () => {
    try {
  const data = await PaymentsServiceApi.createPortalSession(window.location.origin + '/payments');
  if (data && data.url) window.location.href = data.url;
    } catch (e) {
      setActionMsg(e.message || 'Portal error.');
    }
  };

  return (
  <div className="payments-page" data-testid="payments-page">
      <h1 className="pay-title">Payments & Subscription</h1>
      {banner && (
        <div className={`pay-banner ${banner.type}`} data-testid={`banner-${banner.type}`}>{banner.text}</div>
      )}
      {!STRIPE_ENABLED && (
        <div className="pay-banner warn" data-testid="banner-missing-key">Stripe key missing; checkout unavailable.</div>
      )}
      {pollTimeout && !sub?.subscriptionId && (
        <div className="pay-banner warn" data-testid="banner-timeout">Still finalizing subscription – refresh later or contact support.</div>
      )}

      <div className="pay-grid">
        <section className="pay-card" data-testid="current-subscription">
          <h2 className="pay-subtitle">Current Subscription</h2>
          {loading ? (
            <div className="pay-muted">Loading…</div>
          ) : sub && sub.subscriptionId ? (
            <div className="pay-sub-box">
              <div className="pay-row">
                <span>Status</span>
                <strong>{sub.status || 'active'}</strong>
              </div>
              <div className="pay-row">
                <span>Plan</span>
                <strong>{activePlanAlias || activePriceId || '—'}</strong>
              </div>
              {formattedPeriodEnd && (
                <div className="pay-row">
                  <span>Period Ends</span>
                  <strong>{formattedPeriodEnd}</strong>
                </div>
              )}
              <div className="pay-row">
                <span>Cancel At Period End</span>
                <strong>{cancelAtPeriodEnd ? 'Yes' : 'No'}</strong>
              </div>
              <div className="pay-actions">
                {!cancelAtPeriodEnd ? (
                  <button
                    className="btn-danger"
                    disabled={canceling}
                    data-testid="cancel-btn"
                    onClick={handleCancel}
                  >
                    {canceling ? 'Canceling…' : 'Cancel at Period End'}
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    disabled={canceling}
                    data-testid="resume-btn"
                    onClick={async () => { try { await PaymentsServiceApi.resumeSubscription(); await refresh(); setActionMsg('Cancellation removed.'); } catch(e){ setActionMsg(e.message||'Failed to resume'); } }}
                  >
                    Resume
                  </button>
                )}
                <button className="btn-outline" data-testid="portal-btn" onClick={handlePortal}>
                  Manage / Update Payment
                </button>
              </div>
            </div>
          ) : (
            <div className="pay-muted">
              No active subscription. Choose a plan below to get started.
            </div>
          )}
          {actionMsg && <div className="pay-status">{actionMsg}</div>}
        </section>

        {summary && (
          <section className="pay-card">
            <h2 className="pay-subtitle">Payment Summary</h2>
            <div className="pay-row"><span>Total Count</span><strong>{summary.totalCount ?? '—'}</strong></div>
            <div className="pay-row"><span>Total Amount</span><strong>{summary.totalAmount ? (summary.totalAmount/100).toFixed(2) : '—'}</strong></div>
            {summary.lastPaymentAt && <div className="pay-row"><span>Last Payment</span><strong>{new Date(summary.lastPaymentAt).toLocaleDateString()}</strong></div>}
          </section>
        )}

        <section className="pay-card" data-testid="plans-card">
          <h2 className="pay-subtitle">Plans (Checkout)</h2>
          {plansFallback && (
            <div className="pay-muted" data-testid="plans-fallback">Using fallback plans (payments service unreachable).</div>
          )}
          {plansLoading ? <div className="pay-muted">Loading plans…</div> : (
          <div className="plan-list">
            {plans.map(p => {
              const activePlanAlias = sub?.planAlias || sub?.plan_alias;
              const active = p.alias ? (p.alias === activePlanAlias || p.alias === activePriceId) : p.id === activePriceId;
              const params = new URLSearchParams(window.location.search);
              const preselect = params.get('plan');
              const preselected = !sub?.subscriptionId && preselect && (preselect === (p.alias || p.id));
              return (
                <div key={p.id} className={`plan-item ${active ? 'active' : ''} ${preselected ? 'preselected' : ''}`} data-testid={`plan-${p.alias||p.id}`}>
                  <div className="plan-info">
                    <strong>{p.label}</strong>
                    <span className="plan-price">{p.price}</span>
                    <span style={{ fontSize: '.6rem', color: '#64748b' }}>{p.desc}</span>
                    {preselected && !active && (
                      <span style={{ fontSize: '.55rem', color:'#4338ca', fontWeight:600 }}>Selected during signup – redirecting…</span>
                    )}
                  </div>
                  <div className="plan-actions">
                    {active ? (
                      <span className="plan-active-badge">Current</span>
                    ) : (
                      <button
                        className="btn-primary"
                        disabled={redirectingPlan === (p.alias||p.id)}
                        data-testid={`subscribe-${p.alias||p.id}`}
                        onClick={() => handleCheckout(p.alias || p.id)}
                      >
                        {redirectingPlan === (p.alias||p.id) ? 'Redirecting…' : 'Subscribe'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </section>

        <section className="pay-card">
          <h2 className="pay-subtitle">Invoices</h2>
          {loading ? (
            <div className="pay-muted">Loading…</div>
          ) : invoices.length === 0 ? (
            <div className="pay-muted">No invoices.</div>
          ) : (
            <ul className="invoice-list">
              {invoices.map(inv => (
                <li key={inv.id}>
                  <span>{new Date(inv.created * 1000).toLocaleDateString()}</span>
                  <span>{(inv.total / 100).toLocaleString(undefined, {
                    style: 'currency',
                    currency: inv.currency?.toUpperCase() || 'USD'
                  })}</span>
                  {inv.hosted_invoice_url && (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}